import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { gql, useQuery } from "@apollo/client";
import { FileText } from "lucide-react";
import { Button, EmptyState, Pagination } from "../../components/ui";
import {
  BlogListToolbar,
  type ToolbarFilters,
  type StatusTab,
} from "./blog-list/BlogListToolbar";
import { BlogPostRow, type BlogPostRowData } from "./blog-list/BlogPostRow";
import { TagManagerPanel } from "./blog-list/TagManagerPanel";
import { BlogSettingsPanel } from "./blog-list/BlogSettingsPanel";

const PER_PAGE = 15;

const ALL_BLOG_POSTS = gql`
  query AllBlogPostsForListing(
    $page: Int!
    $perPage: Int!
    $filter: BlogPostFilter
  ) {
    allBlogPosts(
      page: $page
      perPage: $perPage
      sortField: "publishAt"
      sortOrder: "DESC"
      filter: $filter
    ) {
      id
      title
      author
      status
      publishAt
      updatedAt
      body
      tagNames
      featuredImageUrl
    }
    _allBlogPostsMeta(filter: $filter) {
      count
    }
  }
`;

interface QueryData {
  allBlogPosts: BlogPostRowData[];
  _allBlogPostsMeta: { count: number };
}

const INITIAL_FILTERS: ToolbarFilters = {
  search: "",
  status: "all",
};

export function BlogListing() {
  const [filters, setFilters] = useState<ToolbarFilters>(INITIAL_FILTERS);
  const [page, setPage] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTagSlug = searchParams.get("tag");

  const handleSelectTag = (slug: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (slug) next.set("tag", slug);
        else next.delete("tag");
        return next;
      },
      { replace: true },
    );
  };

  // Reset to page 0 whenever filters change so the user never lands
  // on a now-empty page after switching tabs or typing in search.
  useEffect(() => {
    setPage(0);
  }, [filters.search, filters.status, selectedTagSlug]);

  const filterVar = useMemo(
    () => buildFilter(filters, selectedTagSlug),
    [filters.search, filters.status, selectedTagSlug],
  );

  const { data, previousData, loading, error, refetch } =
    useQuery<QueryData>(ALL_BLOG_POSTS, {
      variables: { page, perPage: PER_PAGE, filter: filterVar },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    });

  // `previousData` keeps the prior page visible while a new query is
  // in-flight so paging/filtering doesn't flash an empty card.
  const stable = data ?? previousData;
  const posts = stable?.allBlogPosts ?? [];
  const total = stable?._allBlogPostsMeta?.count ?? 0;
  const initialLoad = loading && !stable;

  const filtersActive =
    filters.search.trim().length > 0 ||
    filters.status !== "all" ||
    selectedTagSlug !== null;

  if (error && !stable) {
    return (
      <div className="grid grid-cols-12 gap-3">
        <section className="shadow-card col-span-12 rounded-lg bg-paper-hi p-8 text-center">
          <div className="mb-1 text-[14px] font-medium">
            Couldn't load posts
          </div>
          <div className="mb-4 text-[12.5px] text-ink-4">{error.message}</div>
          <Button variant="primary" onClick={() => refetch()}>
            Try again
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-3">
      <section className="shadow-card col-span-12 lg:col-span-8 overflow-hidden rounded-lg bg-paper-hi">
        <div className="hair-b border-b flex items-center justify-between gap-3 px-5 py-4">
          <div>
            <h2 className="text-[15px] font-semibold">Blog posts</h2>
            <p className="mt-0.5 text-[12px] text-ink-4">
              {filtersActive
                ? `${total} matching ${total === 1 ? "post" : "posts"}`
                : `${total} total ${total === 1 ? "post" : "posts"}`}
            </p>
          </div>
          <BlogListToolbar filters={filters} onFiltersChange={setFilters} />
        </div>

        {initialLoad ? (
          <RowSkeletons />
        ) : posts.length === 0 ? (
          filtersActive ? (
            <EmptyState
              icon={<FileText size={28} strokeWidth={1.5} />}
              heading="No matching posts"
              description="Try a different search term or clear the filter."
            />
          ) : (
            <EmptyState
              icon={<FileText size={28} strokeWidth={1.5} />}
              heading="No blog posts yet"
              description="Write your first post to start filling out your site."
            />
          )
        ) : (
          <div
            className={`divide-hair ${loading ? "opacity-60" : ""} transition-opacity`}
          >
            {posts.map((p) => (
              <BlogPostRow key={p.id} post={p} />
            ))}
          </div>
        )}

        {!initialLoad && total > 0 && (
          <div className="hair-t border-t bg-canvas/40 px-5 py-3">
            <Pagination
              page={page}
              perPage={PER_PAGE}
              total={total}
              onPageChange={setPage}
              unit="post"
            />
          </div>
        )}
      </section>

      <aside className="col-span-12 lg:col-span-4 space-y-3">
        <TagManagerPanel
          selectedSlug={selectedTagSlug}
          onSelect={handleSelectTag}
          onTagsMutated={() => {
            void refetch();
          }}
        />
        <BlogSettingsPanel />
      </aside>
    </div>
  );
}

/**
 * Translate the toolbar's UI state into the BlogPostFilter input the
 * server expects. Returns undefined when no filters are active so the
 * filter argument stays absent on the wire.
 */
function buildFilter(
  filters: ToolbarFilters,
  tagSlug: string | null,
):
  | {
      status?: string;
      q?: string;
      tagSlug?: string;
    }
  | undefined {
  const search = filters.search.trim();
  const tabStatus = mapTabToStatus(filters.status);
  if (!search && !tabStatus && !tagSlug) return undefined;
  return {
    ...(tabStatus ? { status: tabStatus } : {}),
    ...(search ? { q: search } : {}),
    ...(tagSlug ? { tagSlug } : {}),
  };
}

function mapTabToStatus(tab: StatusTab): string | null {
  switch (tab) {
    case "published":
      return "published";
    case "scheduled":
      return "scheduled";
    case "drafts":
      return "draft";
    default:
      return null;
  }
}

function RowSkeletons() {
  return (
    <div className="divide-hair">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="pulse-soft h-14 w-20 flex-shrink-0 rounded bg-canvas-2" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="pulse-soft h-3 w-1/3 rounded bg-canvas-2" />
            <div className="pulse-soft h-4 w-3/4 rounded bg-canvas-2" />
            <div className="pulse-soft h-3 w-1/2 rounded bg-canvas-2" />
          </div>
        </div>
      ))}
    </div>
  );
}
